// #region Imports
import { Context, Dispatch, ReactNode } from 'react';
import { ActionMap, GoMotoFunction } from '../../API/GoMoto.types';
import { ILockerFixture } from '../lockers';
import { Locales } from '../Localization/localization.types';
// #endregion

// #region Setup
/**
 * The object type that is maintained by the admin context and can be
 * consumed by any child of the admin provider.
 */
export interface AdminContextState {
    /**
     * The session ID for the user logging into the admin screen.
     */
    LoginSessionID?: string;
    /**
     * The user ID of the person logging into the admin screen.
     */
    userID?: number;
    /**
     * The current user's pin that is used to log into the admin screen.
     */
    PIN?: string;
    /**
     * The phone number displayed to send the MFA QR Code to. May be masked.
     */
    PhoneNumberMFADisplay?: string | null;
    /**
     * The email address displayed to send the MFA QR Code to. May be masked.
     */
    EmailAddressMFADisplay?: string | null;
    /**
     * Stores the locker fixture that was last changed from an advisor op.
     * Used to trigger refreshes for the key map and kiosk checkins tabs
     * of the admin screens.
     */
    LastLockerChanged?: ILockerFixture;
    /**
     * Whether the logged in user can access the particular dealer from which they initiated the kiosk login.
     */
    canAccessDealer?: boolean;
}

/**
 * The type for the dispatch provider as well as the dispatch function itself.
 */
export type AdminDispatchState = Dispatch<AdminActions>;

/**
 * The context type for the admin state context. It can be undefined so that
 * we can verify anything attempting to consume the context is a child of the context.
 */
export type AdminState = AdminContextState | undefined;

/**
 * The context type for the admin dispatch context. It can be undefined so that
 * we can verify anything attempting to use the dispatch is a child of the context.
 */
export type AdminDispatch = AdminDispatchState | undefined;

/**
 * The admin state context type that is returned after the creation of the state context.
 */
export type AdminStateContextType = Context<AdminState>;

/**
 * The admin dispatch context type that is returned after the creation of the dispatch context.
 */
export type AdminDispatchContextType = Context<AdminDispatch>;
// #endregion

// #region Reducer
/**
 * The admin dispatch types used by our {@link ActionMap} type to distinguish what payload the
 * dispatch action will require.
 */
export enum AdminDispatchTypes {
    /**
     * Dispatches an action to set the current session ID and user ID and clears any MFA values including the PIN.
     *
     * LastLockerChanged is not touched by this action.
     */
    SetLoginSession = 'SET_LOGIN_SESSION',
    /**
     * Clears the entire admin state to undefined values.
     *
     * LastLockerChanged is not touched by this action.
     */
    ClearLoginSession = 'CLEAR_LOGIN_SESSION',
    /**
     * Clears any session ID and user ID and sets the current MFA values including the PIN.
     *
     * LastLockerChanged is not touched by this action.
     */
    SetMFAData = 'SET_MFA_DATA',
    /**
     * Only updates the LastLockerChanged value of the admin state.
     */
    SetLockerChanged = 'SET_LOCKER_CHANGED'
}

/**
 * An object mapping each dispatch type to the payload required for that dispatch action.
 */
export interface AdminPayload {
    [AdminDispatchTypes.SetLoginSession]: { sessionId?: string; userId?: number; canAccessDealer?: boolean; };
    [AdminDispatchTypes.ClearLoginSession]: never;
    [AdminDispatchTypes.SetMFAData]: { PhoneNumberMFADisplay?: string | null; EmailAddressMfaDisplay?: string | null; PIN: string; };
    [AdminDispatchTypes.SetLockerChanged]: ILockerFixture;
}

/**
 * The union type of dispatch actions that results from using {@link ActionMap}.
 */
export type AdminActions = ActionMap<AdminPayload>[keyof ActionMap<AdminPayload>];

/**
 * The admin reducer function type definition. Primarily used by any `useReducer`
 * that uses the function or the function itself.
 */
export type AdminReducerFunction = GoMotoFunction<
    [AdminContextState, AdminActions],
    AdminContextState
>;
// #endregion

// #region Provider
/**
 * The object types for the props of the admin provider component.
 */
export interface AdminProviderProps {
    /**
     * Any element that will render as a child of the admin context.
     */
    children?: ReactNode;
}
// #endregion

// #region Admin Objects
/**
 * The object with the error message that is caught when the admin API calls
 * fail or an error is returned.
 */
export interface APIError {
    /**
     * Contains the error message for why the API call failed.
     */
    message: string;
}

/**
 * The object required by the `ValidateAdminLogin` API call when the user uses a pin
 * to login to the admin screen.
 */
export interface LoginRequest {
    /**
     * The key ID for the kiosk being used.
     */
    kioskKey: string;
    /**
     * The key ID for the dealer associated with the kiosk being used.
     */
    dealerKey: string;
    /**
     * The pin being used by the current user to log into the kiosk admin screen.
     */
    pin: string;
    /**
     * The security code for whether or not MFA is required to log into the admin screen. If
     * the code is undefined, then no MFA is required.
     */
    code?: string;
}

/**
 * The object requried by the `ValidateKioskMFAQRCodeLogin` API call when the
 * user uses a QR code to login to the admin screen.
 */
export interface QrLoginRequest {
    /**
     * The key ID for the kiosk being used.
     */
    kioskKey: string;
    /**
     * The key for the QR code being used.
     */
    qrCode: string;
}

/**
 * The object required by the `RequestMFACode` API call when the user enters a pin and also
 * requires a MFA code to finish logging into the admin screen.
 */
export interface MFACodeRequest {
    /**
     * The key ID for the kiosk being used.
     */
    kioskKey: string;
    /**
     * The key ID for the dealer associated with the kiosk being used.
     */
    dealerKey: string;
    /**
     * The pin being used by the current user to log into the kiosk admin screen.
     */
    pin: string;
    /**
     * The method that the MFA code will be delivered to the user.
     */
    deliveryMethod: string;
}

/**
 * Associates the back-end's number code for the language to the actual language. This is used specifically by
 * `useResolveLocale` to convert the number code via this enum into the correct {@link Locales} type for the Localization provider.
 */
export enum Language {
    /**
     * None mirrors the none value in the {@link Locales} enum. Locale defaults none to `en-us`, but
     * this also represents any numerical code that does not associate itself with any of the actually supported
     * languages.
     */
    none = 0,
    /**
     * Represents the US English localization. This is also used for Canadian English as well.
     */
    EnUs = 1,
    /**
     * Represents the US Spanish localization.
     */
    EsUs = 2,
    /**
     * Represents the Canadian French localization.
     */
    FrCa = 3
}

/**
 * The object type returned from the back-end during a login attempt. Not all props on this object
 * are used for every API call return, but some API call returned objects overlap on the properties returned.
 * This object acts as a catch-all.
 */
export interface AdminLoginResult {
    /**
     * Whether an error occurred and was returned by the API call.
     */
    error: boolean;
    /**
     * The session ID for the user logging into the admin screen after validating their login credentials.
     */
    sessionId?: string;
    /**
     * The ID of the user logging into the admin screen after validating their login credentials.
     */
    userId?: number;
    /**
     * The dealer key ID of the dealer associated with the kiosk being used.
     */
    dealerKey?: string;
    /**
     * The message returned by the API call during an error. This is typically a 403 error.
     */
    message?: string;
    /**
     * If MFA is required and will be sent to a phone number, this is the phone number it will be sent to.
     * The phone number can be masked to protect PII.
     */
    phoneNumberMfaDisplay?: string;
    /**
     * If MFA is required and will be sent to an emaila ddress, this is the email address it will be sent to.
     * The email address can be masked to protect PII.
     */
    emailAddressMfaDisplay?: string;
    /**
     * Converts the numerical language code from the back-end into the {@link Language} enum so we can then
     * convert it to the correct {@link Locales} type for the Localization provider.
     */
    language?: Language;
    /**
     * Whether the logged in user can access the particular dealer from which they initiated the kiosk login.
     */
    canAccessDealer?: boolean;
}

/**
 * The object type returned by the back-end when the user requests another MFA code to be sent to the phone number
 * or email address associated with the user for MFA. This object is exclusively for handling errors and not any actual returned
 * data from a successful request.
 */
export interface RequestMFACodeResult {
    /**
     * Whether an error occurred and was returned by the API call.
     */
    error: boolean;
    /**
     * The message returned by the API call during an error.
     */
    message?: string;
}
// #endregion

// #region Hooks
/**
 * The function type for the `useAdminState` hook. Takes no arguments and returns the current admin state.
 */
export type AdminStateHook = GoMotoFunction<
    [void],
    AdminContextState
>;

/**
 * The function type for the `useAdminDispatch` hook. Takes no arguments and returns a dispatch function for the admin context.
 */
export type AdminDispatchHook = GoMotoFunction<
    [void],
    AdminDispatchState
>;

/**
 * The function type for the `useAdminLogin` hook. Takes no arguments and returns a function that takes 1 argument of {@link LoginRequest} type and returns
 * a Promise with the {@link AdminLoginResult} type.
 */
export type AdminLoginHook = GoMotoFunction<
    [void],
    GoMotoFunction<[LoginRequest], Promise<AdminLoginResult>>
>;

/**
 * The function type for the `useAdminQrLogin` hook. Takes no arguments and returns a function that takes 1 argument of {@link QrLoginRequest} type and returns
 * a Promise with the {@link AdminLoginResult} type.
 */
export type AdminQrLoginHook = GoMotoFunction<
    [void],
    GoMotoFunction<[QrLoginRequest], Promise<AdminLoginResult>>
>;

/**
 * The function type for the `useRequestMFACode` hook. Takes no arguments and returns a function that takes 1 argument of {@link MFACodeRequest} type and returns
 * a Promise with the {@link RequestMFACodeResult} type.
 */
export type RequestMFACodeHook = GoMotoFunction<
    [void],
    GoMotoFunction<[MFACodeRequest], Promise<RequestMFACodeResult>>
>;

/**
 * The function type for the `useAdminLogout` hook. Takes no arguments and returns a function that takes 1 argument of a boolean type to indicate whether auto-logout
 * should be enabled and returns a Promise with the type of void.
 */
export type AdminLogoutHook = GoMotoFunction<
    [void],
    GoMotoFunction<[boolean], Promise<void>>
>;
// #endregion

// #region Functions
/**
 * Converts the optional argument of {@link Language} type into the correct {@link Locales} type associated with it
 * or to undefined if no association can be made. Since the argument is optional, no argument passed to it will result
 * in the function returning undefined.
 */
export type ResolveLocaleFunction = GoMotoFunction<
    [Language?],
    Locales | undefined
>;
// #endregion